import {
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MyContext } from "../../../context/MyContext";
import { useSocratesInterview } from "../../../hooks/useSocratesInterview";
import { useSocratesV2 } from "../../../context/SocratesV2Context";
import socratesApi from "../../../api/socratesApi";

/**
 * SocratesV2 View Hook
 * 튜터V2와 동일한 레이아웃을 위한 뷰 로직
 */
const useSocratesV2View = () => {
  const navigate = useNavigate();
  const { id: interviewId } = useParams<{ id: string }>();
  const { isMobile, user, setIsOpenTicketAlert } = useContext(MyContext);
  const { state, dispatch } = useSocratesV2();

  const {
    interview,
    messages,
    loading,
    isRestarting,
    createInterview,
    loadInterview,
  } = useSocratesInterview();

  // Context state에서 직접 isGeneratingResult 가져오기 (리렌더링 보장)
  const isGeneratingResult = state.isGeneratingResult;

  // 면접 카드 클릭 시 로딩 상태
  const [isStartingInterview, setIsStartingInterview] = useState(false);

  // 반응형 체크 (md 이상)
  const isMd = !isMobile;

  // 로그인 여부
  const isLoggedIn = !!user?.id;

  // 현재 면접 ID
  const currentInterviewId = interviewId ? Number(interviewId) : null;

  // 이전 인터뷰 ID 추적 (네비게이션 감지용)
  const prevInterviewIdRef = useRef<number | null>(null);

  // 인터뷰 ID가 있을 때 자동으로 인터뷰 데이터 로드
  useEffect(() => {
    const prevInterviewId = prevInterviewIdRef.current;
    const isInterviewIdChanged = currentInterviewId !== prevInterviewId;

    // 인터뷰 ID가 변경된 경우 (다른 면접으로 이동, cloneInterview 후 네비게이션 등)
    if (isInterviewIdChanged && currentInterviewId && isLoggedIn) {
      // isRestarting이 true인 상태에서 ID가 변경되면 해제 (cloneInterview 후 네비게이션)
      if (isRestarting) {
        dispatch({ type: "SET_RESTARTING", payload: false });
      }
      loadInterview(currentInterviewId);
      prevInterviewIdRef.current = currentInterviewId;
    }
    // 인터뷰 ID는 같지만 아직 로드되지 않은 경우 (새로고침 등)
    else if (
      !isInterviewIdChanged &&
      currentInterviewId &&
      isLoggedIn &&
      !isRestarting &&
      !interview
    ) {
      loadInterview(currentInterviewId);
    }
  }, [
    currentInterviewId,
    isLoggedIn,
    loadInterview,
    isRestarting,
    interview,
    dispatch,
  ]);

  // 메시지 존재 여부 체크
  const hasAnyMessages = useMemo(() => {
    if (!currentInterviewId) return false;
    return messages && messages.length > 0;
  }, [currentInterviewId, messages]);

  // 면접 진행률 계산 (0-100)
  const interviewProgress = useMemo(() => {
    // 면접이 없거나, 메시지가 없으면 progress 표시 안함
    if (!currentInterviewId || !messages || messages.length === 0) {
      return undefined;
    }

    // 면접 완료 상태면 100% (🥳 이모지 표시)
    if (interview?.status === "completed") {
      return 100;
    }

    // 현재 질문 수 (assistant 메시지 수)
    const currentQuestions = messages.filter(
      (msg: any) => msg.role === "assistant"
    ).length;

    // 진행률 계산: 1번째 질문 = 0%, 2번째 = 10%, ... 10번째 = 90%, 완료 = 100%
    const progress = Math.min((currentQuestions - 1) * 10, 90);

    return progress;
  }, [currentInterviewId, messages, interview]);

  /**
   * 로그인 버튼 클릭 핸들러
   */
  const handleLoginClick = useCallback(() => {
    window.location.assign(
      `${process.env.REACT_APP_LOGIN_URL}/?referer_url=${process.env.REACT_APP_REFERER_URL}/socrates`
    );
  }, []);

  /**
   * 나만의 소크라테스 만들기 버튼 클릭 핸들러
   */
  const handleCreateSocrates = useCallback(() => {
    navigate("/socrates/new");
  }, [navigate]);

  /**
   * 면접 카드 클릭 핸들러 - 기존 면접을 복제하여 신규 면접 생성
   */
  const handleStartInterview = useCallback(
    async (interviewId: number) => {
      try {
        setIsStartingInterview(true);
        const result = await socratesApi.cloneInterview(interviewId, user?.id);
        if (result?.interviewId) {
          navigate(`/socrates/${result.interviewId}`);
        }
      } catch (error) {
        console.error("면접 복제 실패:", error);
      } finally {
        setIsStartingInterview(false);
      }
    },
    [user?.id, navigate]
  );

  /**
   * 카드 클릭 핸들러 - 면접 생성
   */
  const handleCardClick = useCallback(
    async (description: string) => {
      // 로그인 검증
      if (!user || !user.id) {
        window.location.assign(
          `${process.env.REACT_APP_LOGIN_URL}/?referer_url=${process.env.REACT_APP_REFERER_URL}/socrates`
        );
        return;
      }

      // 사용량 검증
      if (user.socra_cnt === 0) {
        setIsOpenTicketAlert(true);
        return;
      }

      try {
        // FormData 생성
        const formData = new FormData();
        formData.append("userId", user.id.toString());
        formData.append("interviewType", "일반 면접");
        formData.append("interviewSubject", description);
        formData.append("targetInstitution", "소크라테스");
        formData.append("keywords", "과학");
        formData.append("personalStatementText", description);

        const result = await createInterview(formData);

        if (result && result.interviewId) {
          navigate(`/socrates/${result.interviewId}`);
        }
      } catch (error) {
        console.error("면접 생성 실패:", error);
      }
    },
    [user, createInterview, navigate, setIsOpenTicketAlert]
  );

  /**
   * 샘플 면접 클릭 핸들러
   */
  const handleSampleClick = useCallback(
    async (sample: any) => {
      // 로그인 검증
      if (!user || !user.id) {
        window.location.assign(
          `${process.env.REACT_APP_LOGIN_URL}/?referer_url=${process.env.REACT_APP_REFERER_URL}/socrates`
        );
        return;
      }

      // 사용량 검증
      if (user.socra_cnt === 0) {
        setIsOpenTicketAlert(true);
        return;
      }

      try {
        const formData = new FormData();
        formData.append("userId", user.id.toString());
        formData.append("interviewType", "체험 면접");
        formData.append("interviewSubject", sample.title || "소크라테스 체험");
        formData.append("targetInstitution", sample.title || "소크라테스 체험");
        formData.append("keywords", "과학");

        const result = await createInterview(formData);

        if (result && result.interviewId) {
          navigate(`/socrates/${result.interviewId}`);
        }
      } catch (error) {
        console.error("샘플 면접 생성 실패:", error);
      }
    },
    [user, createInterview, navigate, setIsOpenTicketAlert]
  );

  return {
    isMd,
    isLoggedIn,
    hasAnyMessages,
    currentInterviewId,
    isRestarting,
    isGeneratingResult,
    isStartingInterview,
    interviewProgress,
    handleCardClick,
    handleSampleClick,
    handleLoginClick,
    handleCreateSocrates,
    handleStartInterview,
    interview,
    messages,
  };
};

export default useSocratesV2View;
